/**
 * The card details page (spec #316, Lavish-approved 2026-11-01 mockup
 * `docs/design/editor-dashboard-2026-11/card-details.html`): everything an
 * editor needs on an accepted card, replacing the Trello card link entirely.
 * Mounted inside `CardFocusView`'s `HubPage` (EditorScreen.tsx) for the two
 * stages it covers - `in_production` ("just accepted") and
 * `changes_requested` ("in revision"); every other stage keeps its existing
 * tile (`CardFocusTile`).
 *
 * Fetches its own data from `GET /api/editor/work/:id/details` rather than
 * reusing the dashboard's `EditorCard` - that endpoint answers with the
 * reduced offer preview while a card is still only offered, and the full
 * payload (briefing, assets, job panel, checklist) once assigned; this screen
 * only ever renders the `"full"` shape, since the stages it mounts for are
 * never `"offered"`.
 *
 * @module web/screens/EditorCardDetails
 */
import { ClientFeedback } from "./ClientFeedback.tsx";
import { GiveBackConfirm } from "./GiveBackConfirm.tsx";
import { ReleaseCardConfirm } from "./FixedWeekPanel.tsx";
import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError, get, post } from "../api/client.ts";
import { DriveHandin } from "./DriveHandin.tsx";
import type { CardDetailsChecklistItem, CardDetailsFull, CardDetailsResponse } from "../api/types.ts";
import { useI18n, type MessageKey } from "../i18n/index.tsx";
import { initials } from "./editorFormat.ts";
import {
  assetIconText,
  formatBytes,
  jobDueSubLabel,
  jobProgressPercent,
  jobTimeLeftLabel,
  languageFactLabel,
  lengthFactLabel,
  priceAmountLabel,
  priceSubLabel,
  typeFactLabel,
} from "./cardDetailsFormat.ts";
import { BriefingText, LinkifiedText } from "./safeMarkdown.tsx";
import { copyPlainText } from "./copyPlainText.ts";

type OnActed = (note?: string) => void;

type LoadState = { kind: "loading" } | { kind: "error" } | { kind: "ready"; data: CardDetailsFull };

/** The Loom block's sub line - where the tap goes ("loom.com"), so it never looks like a dead player. */
function loomHost(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return "";
  }
}

function toFullState(resp: CardDetailsResponse): LoadState {
  // The two stages this screen mounts for are never "offered" - a reduced
  // preview here means the card moved on between the dashboard's fetch and
  // this one; the same graceful "not available" state as an unknown id.
  return resp.kind === "full" ? { kind: "ready", data: resp } : { kind: "error" };
}

/** How long "Copied" stays on the button. */
const COPY_FEEDBACK_MS = 2000;

/** Copies the briefing exactly as stored: the version note and every closed section with it. */
function CopyBriefingButton({ text }: { text: string }) {
  const { t } = useI18n();
  const [status, setStatus] = useState<"idle" | "copied" | "failed">("idle");
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
  }, []);

  async function copy() {
    const ok = await copyPlainText(text);
    setStatus(ok ? "copied" : "failed");
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setStatus("idle"), COPY_FEEDBACK_MS);
  }

  const label = status === "copied" ? "cardDetails.copiedBriefing" : status === "failed" ? "cardDetails.copyBriefingFailed" : "cardDetails.copyBriefing";
  return (
    <button type="button" className="cd-btn ghost cd-btn-small" data-copy-briefing onClick={() => void copy()}>
      <span data-copy-briefing-status aria-live="polite">{t(label)}</span>
    </button>
  );
}

export function CardDetailsPage({ workItemId, onActed }: { workItemId: string; onActed: OnActed }) {
  const i18n = useI18n();
  const { t } = i18n;
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [busy, setBusy] = useState<"submit" | "give-back" | "upload" | null>(null);
  const [driveOpen, setDriveOpen] = useState(false);
  const [confirmingGiveBack, setConfirmingGiveBack] = useState(false);
  const [confirmingPush, setConfirmingPush] = useState(false);
  const [actionError, setActionError] = useState<MessageKey | null>(null);

  const load = useCallback(async () => {
    try {
      const resp = await get<CardDetailsResponse>(`/api/editor/work/${workItemId}/details`);
      setState(toFullState(resp));
    } catch {
      setState({ kind: "error" });
    }
  }, [workItemId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function toggleItem(key: string, ticked: boolean) {
    setState((prev) =>
      prev.kind === "ready"
        ? { ...prev, data: { ...prev.data, checklist: { ...prev.data.checklist, items: prev.data.checklist.items.map((it) => (it.key === key ? { ...it, ticked } : it)) } } }
        : prev,
    );
    try {
      await post(`/api/editor/work/${workItemId}/checklist`, { key, ticked });
    } catch {
      setState((prev) =>
        prev.kind === "ready"
          ? { ...prev, data: { ...prev.data, checklist: { ...prev.data.checklist, items: prev.data.checklist.items.map((it) => (it.key === key ? { ...it, ticked: !ticked } : it)) } } }
          : prev,
      );
      setActionError("cardDetails.checklistError");
    }
  }

  /** Spec #317: "this item is wrong" opens an Ops item; the button then reads "Reported". */
  async function flagItem(key: string) {
    try {
      await post(`/api/editor/work/${workItemId}/checklist/flag`, { key });
      setState((prev) =>
        prev.kind === "ready"
          ? { ...prev, data: { ...prev.data, checklist: { ...prev.data.checklist, items: prev.data.checklist.items.map((it) => (it.key === key ? { ...it, flagged: true } : it)) } } }
          : prev,
      );
    } catch {
      setActionError("cardDetails.flagError");
    }
  }

  function checklistRow(item: CardDetailsChecklistItem) {
    return (
      <div key={item.key} className="cd-check-row" data-checklist-item={item.key}>
        <label className={`cd-check${item.ticked ? " done" : ""}`}>
          <input type="checkbox" checked={item.ticked} onChange={(e) => void toggleItem(item.key, e.target.checked)} />
          <i aria-hidden="true">{item.ticked ? "✓" : ""}</i>
          <span>{item.text}</span>
        </label>
        <button
          type="button"
          className="cd-flag"
          data-checklist-flag={item.key}
          disabled={item.flagged}
          aria-label={item.flagged ? t("cardDetails.flaggedItem") : t("cardDetails.flagItemAria", { item: item.text })}
          onClick={() => void flagItem(item.key)}
        >
          {item.flagged ? t("cardDetails.flaggedItem") : t("cardDetails.flagItem")}
        </button>
      </div>
    );
  }

  function submitErrorKey(err: unknown): MessageKey {
    const code = err instanceof ApiError ? err.message : null;
    if (code === "no_trello_card" || code === "freeframe_not_configured") return "dashboard.submitErrNoFolder";
    if (code === "freeframe_unavailable") return "dashboard.submitErrUnavailable";
    if (code === "revision_open") return "dashboard.submitErrRevisionOpen";
    if (code === "stage_changed") return "dashboard.submitErrStageChanged";
    return "cardDetails.submitError";
  }

  async function submit() {
    if (busy) return;
    if (state.kind === "ready" && state.data.driveDelivery) {
      setDriveOpen(true);
      return;
    }
    setBusy("submit");
    setActionError(null);
    const tab = window.open("about:blank", "_blank");
    try {
      const result = await post<{ ok: boolean; uploadUrl: string | null }>(`/api/editor/work/${workItemId}/submit`);
      const target = result.uploadUrl ?? (state.kind === "ready" ? state.data.handinUrl : null);
      if (target) {
        if (tab) tab.location.href = target;
        else window.open(target, "_blank", "noopener,noreferrer");
      } else tab?.close();
    } catch (err) {
      tab?.close();
      setActionError(submitErrorKey(err));
    } finally {
      setBusy(null);
      onActed();
    }
  }

  async function giveBack() {
    if (busy) return;
    setBusy("give-back");
    setActionError(null);
    try {
      const result = await post<{ ok: boolean; message?: string }>(`/api/editor/work/${workItemId}/give-back`);
      onActed(result.message);
    } catch {
      setActionError("cardDetails.giveBackError");
      onActed();
    } finally {
      setBusy(null);
    }
  }

  function recordRevisionHandin() {
    if (busy) return;
    setBusy("upload");
    void post(`/api/editor/work/${workItemId}/submit`)
      .catch(() => {
        /* best-effort: the work lives in FreeFrame regardless; the next poll reconciles */
      })
      .finally(() => {
        setBusy(null);
        onActed();
      });
  }

  if (state.kind === "loading") return <p className="ed-muted">{t("common.loading")}</p>;
  if (state.kind === "error") {
    return (
      <div className="editor-offer-card editor-offer-empty" data-screen-state="card-unavailable">
        <p>{t("cardDetails.unavailableBody")}</p>
      </div>
    );
  }

  const d = state.data;
  const inRevision = d.stage === "changes_requested";
  const uploadUrl = d.uploadOpen ? d.uploadUrl : null;
  const doneCount = d.checklist.items.filter((it) => it.ticked).length;
  const type = typeFactLabel(d.job.format);
  const length = lengthFactLabel(d.job, t);
  const language = languageFactLabel(d.job.language);
  const commentsHref = d.reviewUrl ?? d.handinUrl;

  return (
    <div className="cd-page" data-screen="editor-card-details">
      <div className="cd-head">
        <button type="button" className="cd-back" onClick={() => window.location.assign("/editor")}>
          ← {t("cardDetails.back")}
        </button>
        <div className="cd-title-row">
          <span className="cd-brand-avatar" aria-hidden="true">{initials(d.brandName)}</span>
          <div>
            <h1 className="cd-title">{d.title}</h1>
            <div className="cd-sub">
              {t("cardDetails.acceptedOn", { brand: d.brandName, when: d.acceptedAt ? i18n.dateTime(d.acceptedAt, { weekday: "short", hour: "2-digit", minute: "2-digit" }) : "" })}
            </div>
          </div>
        </div>
        <div className="cd-chips">
          {inRevision && <span className="cd-chip warn"><span className="dot" />{t("cardDetails.chipInRevision", { round: d.checklist.round ?? 1 })}</span>}
          {!inRevision && <span className="cd-chip"><span className="dot" />{uploadUrl ? t("dashboard.uploadOpen") : t("cardDetails.chipInProduction")}</span>}
          {d.requestedForYou && <span className="cd-chip ask">{t("cardDetails.chipRequested")}</span>}
        </div>
      </div>

      <div className="cd-grid">
        <div className="cd-col">
          <ClientFeedback workItemId={d.id} alongsideFreeframe={inRevision} />

          <section className="cd-panel">
            <div className="cd-tag">
              <span>{t("cardDetails.briefing")}</span>
              {d.briefing.scriptMd && <CopyBriefingButton text={d.briefing.scriptMd} />}
            </div>
            {d.offerNote.text && (
              <div className="cd-note cd-note-top">
                <div className="cd-note-head"><span>{t("cardDetails.noteFromAditor", { brand: d.brandName })}</span></div>
                <p className="cd-note-text"><LinkifiedText text={d.offerNote.text} /></p>
                {d.offerNote.loomUrl && (
                  <a className="cd-loom" href={d.offerNote.loomUrl} target="_blank" rel="noopener noreferrer">
                    <span className="cd-loom-thumb" aria-hidden="true" />
                    <span><b>{t("cardDetails.watchLoom")}</b><small>{loomHost(d.offerNote.loomUrl)}</small></span>
                  </a>
                )}
              </div>
            )}
            <div className="cd-brief">{d.briefing.scriptMd && <BriefingText text={d.briefing.scriptMd} noteLabel={t("cardDetails.versionNote")} openAllLabel={t("cardDetails.openAllSections")} closeAllLabel={t("cardDetails.closeAllSections")} />}</div>
          </section>

          <section className="cd-panel">
            <div className="cd-tag">
              <span>{t("cardDetails.assets")}</span>
              {d.assets.length > 0 && <span>{d.assets.length}</span>}
            </div>
            {d.assets.length === 0 ? (
              <p className="ed-muted">{t("cardDetails.noAssets")}</p>
            ) : (
              <div className="cd-assets">
                {d.assets.map((a) => (
                  <a key={a.key} className="cd-asset" href={a.href} target="_blank" rel="noopener noreferrer">
                    <span className="cd-asset-icon">{assetIconText(a)}</span>
                    <span>
                      <b>{a.label}</b>
                      <small>{a.kind === "attachment" ? (a.bytes != null ? `${t("cardDetails.attached")} · ${formatBytes(a.bytes)}` : t("cardDetails.attached")) : new URL(a.href).hostname.replace(/^www\./, "")}</small>
                    </span>
                    <span className="cd-asset-go" aria-hidden="true">↗</span>
                  </a>
                ))}
              </div>
            )}
          </section>

          {d.recentlyDelivered.length > 0 && (
            <section className="cd-panel cd-extra">
              <div className="cd-tag"><span>{t("cardDetails.additionalInfo")}</span></div>
              <div className="cd-sub-tag">{t("cardDetails.recentlyDelivered", { brand: d.brandName })}</div>
              <div className="cd-refs">
                {d.recentlyDelivered.map((r) => (
                  <a className="cd-ref" key={r.id} href={r.href} target="_blank" rel="noopener noreferrer">
                    <p>{r.title}</p>
                    <small>{r.label} ↗</small>
                  </a>
                ))}
              </div>
            </section>
          )}
        </div>

        <div className="cd-col cd-side">
          <section className="cd-panel">
            <div className="cd-tag"><span>{t("cardDetails.yourCard")}</span></div>
            <div className="cd-job-top">
              <div className="cd-price">
                <strong>{priceAmountLabel(d.job.priceCents, i18n)}</strong>
                <small>{priceSubLabel(d.job.priceKind, d.brandName, t)}</small>
              </div>
              <div className="cd-due">
                <strong>{jobTimeLeftLabel(d.job.deadlineAt, t, d.job.graceEndsAt)}</strong>
                <small>{jobDueSubLabel(d.job, i18n)}</small>
              </div>
            </div>
            <div className="cd-facts">
              {type && <div className="cd-fact"><small>{t("cardDetails.typeFact")}</small><b>{type}</b></div>}
              {length && <div className="cd-fact"><small>{t("cardDetails.lengthFact")}</small><b>{length}</b></div>}
              {language && <div className="cd-fact"><small>{t("cardDetails.languageFact")}</small><b>{language}</b></div>}
            </div>
            <div className="cd-bar"><span style={{ width: `${jobProgressPercent(d.job)}%` }} /></div>
            {d.job.handedIn && (
              <div className="cd-handed">
                {t(d.job.handedIn.onTime ? "cardDetails.handedInOnTime" : "cardDetails.handedInLate", { when: i18n.dateTime(d.job.handedIn.at, { weekday: "short", hour: "2-digit", minute: "2-digit" }) })}
              </div>
            )}

            {d.checklist.kind === "briefing" ? (
              <div className="cd-cl">
                <div className="cd-cl-head">
                  <span>{t("cardDetails.checklistTitle")}</span>
                  <span className="cd-cl-count">{t("cardDetails.checklistCount", { done: doneCount, total: d.checklist.items.length })}</span>
                </div>
                {d.checklist.items.map((item) => checklistRow(item))}
              </div>
            ) : (
              <div className="cd-cl">
                <div className="cd-cl-head">
                  <span>{t("cardDetails.requestedChangesTitle")}</span>
                  <span>{t("cardDetails.requestedChangesRound", { round: d.checklist.round ?? 1 })}</span>
                </div>
                {d.checklist.items.map((item) => checklistRow(item))}
                {commentsHref && (
                  <a className="cd-cl-source" href={commentsHref} target="_blank" rel="noopener noreferrer">
                    {t("cardDetails.clientComments", { brand: d.brandName })}
                  </a>
                )}
              </div>
            )}

            {actionError && <p className="editor-offer-error">{t(actionError)}</p>}

            {inRevision && d.driveDelivery ? (
              <DriveHandin workItemId={d.id} title={d.title} onDone={() => onActed()} primaryClass="cd-btn primary" buttonClass="cd-btn ghost" />
            ) : inRevision ? (
              <>
                <div className="cd-actions">
                  {d.handinUrl ? (
                    <a className="cd-btn primary" href={d.handinUrl} target="_blank" rel="noopener noreferrer" onClick={recordRevisionHandin}>
                      {busy === "upload" ? t("cardDetails.uploading") : t("cardDetails.uploadNewVersion")}
                    </a>
                  ) : (
                    <span className="cd-btn primary" aria-disabled="true">{t("cardDetails.uploadNewVersion")}</span>
                  )}
                </div>
                {!d.handinUrl && <p className="cd-hint">{t("cardDetails.noHandinLink")}</p>}
                <p className="cd-hint">{t("cardDetails.hintRevision")}</p>
              </>
            ) : (
              <>
                {confirmingGiveBack && d.giveBack ? (
                  <GiveBackConfirm
                    terms={d.giveBack}
                    busy={busy === "give-back"}
                    onConfirm={() => void giveBack()}
                    onKeep={() => setConfirmingGiveBack(false)}
                    buttonClass="cd-btn ghost"
                    primaryClass="cd-btn primary"
                  />
                ) : confirmingPush && d.pushToDispatch ? (
                  <ReleaseCardConfirm
                    workItemId={d.id}
                    title={d.title}
                    terms={d.pushToDispatch}
                    onDone={() => window.location.assign("/editor")}
                    onCancel={() => setConfirmingPush(false)}
                    primaryClass="cd-btn primary"
                    buttonClass="cd-btn ghost"
                  />
                ) : d.driveDelivery && driveOpen ? (
                  <DriveHandin workItemId={d.id} title={d.title} onDone={() => onActed()} onCancel={() => setDriveOpen(false)} primaryClass="cd-btn primary" buttonClass="cd-btn ghost" />
                ) : (
                  <div className="cd-actions">
                    {uploadUrl ? (
                      <a className="cd-btn primary" href={uploadUrl} target="_blank" rel="noopener noreferrer">{t("dashboard.uploadOpenLink")}</a>
                    ) : (
                      <button type="button" className="cd-btn primary" disabled={busy !== null} onClick={() => void submit()}>
                        {d.driveDelivery ? t("dashboard.driveSubmit") : busy === "submit" ? t("cardDetails.submitting") : t("cardDetails.submit")}
                      </button>
                    )}
                    {d.giveBack && (
                      <button type="button" className="cd-btn ghost" disabled={busy !== null} onClick={() => setConfirmingGiveBack(true)}>{t("cardDetails.giveBack")}</button>
                    )}
                    {d.pushToDispatch && (
                      <button type="button" className="cd-btn ghost" data-release-card disabled={busy !== null} onClick={() => setConfirmingPush(true)}>{t("fixedWeek.releaseCard")}</button>
                    )}
                  </div>
                )}
                {!(d.driveDelivery && driveOpen) && !confirmingPush && <p className="cd-hint">{d.driveDelivery ? t("dashboard.driveHint") : uploadUrl ? t("dashboard.uploadOpenNote") : t(d.job.deadlineAt ? "cardDetails.hintProduction" : "cardDetails.hintProductionNoClock")}</p>}
              </>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
